import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SrIcon } from "@/components/ui/SrIcon";
import { type WidgetDefinition } from "@/lib/widget-registry";
import { cn } from "@/lib/utils";

const CATEGORY_COLORS: Record<string, string> = {
  personal: "bg-blue-500/10 text-blue-500",
  team: "bg-emerald-500/10 text-emerald-500",
  portfolio: "bg-amber-500/10 text-amber-500",
  admin: "bg-purple-500/10 text-purple-500",
};

const CATEGORY_LABELS: Record<string, string> = {
  personal: "Personlig",
  team: "Team",
  portfolio: "Portefølje",
  admin: "Admin",
};

const CATEGORY_ORDER: WidgetDefinition["category"][] = [
  "personal",
  "team",
  "portfolio",
  "admin",
];

const SIZE_LABEL: Record<"full" | "half" | "third", string> = {
  full: "Full",
  half: "½",
  third: "⅓",
};

/**
 * VIB-708-followup: Liste over widgets admin kan legge til, gruppert
 * etter kategori med beskrivelse + default-størrelse. Erstatter den
 * gamle flate listen som bare viste navn + kategori-tag.
 */
export function AvailableWidgetsSection({
  available,
  onAdd,
}: {
  available: WidgetDefinition[];
  onAdd: (widget: WidgetDefinition) => void;
}) {
  if (available.length === 0) {
    return (
      <div className="mt-6 p-4 border rounded-md text-center text-sm text-muted-foreground">
        Alle tilgjengelige widgets er lagt til.
      </div>
    );
  }

  const byCategory = new Map<WidgetDefinition["category"], WidgetDefinition[]>();
  for (const w of available) {
    const list = byCategory.get(w.category) ?? [];
    list.push(w);
    byCategory.set(w.category, list);
  }

  return (
    <div className="mt-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          Tilgjengelige widgets
        </h3>
        <span className="text-[10px] text-muted-foreground">
          {available.length} igjen å legge til
        </span>
      </div>
      {CATEGORY_ORDER.map((cat) => {
        const items = byCategory.get(cat);
        if (!items?.length) return null;
        return (
          <div key={cat} className="space-y-1.5">
            <div className="flex items-center gap-2">
              <Badge
                variant="secondary"
                className={cn("text-[10px]", CATEGORY_COLORS[cat])}
              >
                {CATEGORY_LABELS[cat]}
              </Badge>
              <span className="text-[10px] text-muted-foreground">
                {items.length} widget{items.length === 1 ? "" : "s"}
              </span>
            </div>
            {items.map((w) => (
              <Card
                key={w.key}
                className="cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-colors"
                onClick={() => onAdd(w)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onAdd(w);
                  }
                }}
              >
                <CardContent className="p-3 flex items-start gap-3">
                  <SrIcon
                    name="plus"
                    className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{w.name}</span>
                      <Badge variant="outline" className="text-[10px] h-4 font-mono">
                        {SIZE_LABEL[w.defaultSize]}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                      {w.description}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        );
      })}
    </div>
  );
}
